/*
 * hp-collection-promo -- keeps each promo tile's heading WHOLE on mobile.
 *
 * Node 5004:150 sets the one-column heading at 18.745 (-5% tracking) in a 146
 * box. Eurostile Extended is wide, so on a phone narrower than the node's 390
 * the longest word can stop fitting ("STANDING" is ~7em) and would break
 * mid-word. This shrinks the heading just enough to keep every word whole,
 * down to FLOOR, and does nothing else: a heading that fits stays at the
 * node's size. (It used to GROW short headings up to 24, because the old
 * mobile node drew no text. The revised node has a size, so that is gone.)
 *
 * How: every word's width is measured at BASE with a Range (one rect per word
 * while `overflow-wrap` is `normal`) against the CONTENT box -- not
 * `scrollWidth`, which ignores a word spilling into the inline padding and so
 * accepted a size that split "STANDIN / G". Width scales linearly with font
 * size (the tracking is in em, so it scales too), so the fitting size is
 * BASE x content / widest word, less a half-pixel margin, floored to 0.25.
 *
 * FAILS SAFE: the stylesheet's size (the node's) is what shows without this.
 * Re-fits after the campaign font loads (Adobe's kit arrives late and changes
 * every width), on resize, and when tiles are added or replaced (infinite
 * scroll, filtering), via a MutationObserver on the grid.
 */
(() => {
  const BASE = 18.745; // the node's size, the stylesheet's, and where measuring starts
  const FLOOR = 14; // never smaller; past this the heading wants 2 columns
  const MOBILE = window.matchMedia('(max-width: 749px)');
  const SELECTOR = '.hp-collection-promo:not(.hp-collection-promo--m2) .hp-collection-promo__heading';

  const widestWord = (heading) => {
    let widest = 0;
    const walker = document.createTreeWalker(heading, NodeFilter.SHOW_TEXT);
    for (let node = walker.nextNode(); node; node = walker.nextNode()) {
      const re = /\S+/g;
      let m;
      while ((m = re.exec(node.textContent))) {
        const range = document.createRange();
        range.setStart(node, m.index);
        range.setEnd(node, m.index + m[0].length);
        widest = Math.max(widest, range.getBoundingClientRect().width);
      }
    }
    return widest;
  };

  const fitOne = (heading) => {
    heading.style.removeProperty('--hp-promo-fit');
    if (!MOBILE.matches) return;

    const cs = getComputedStyle(heading);
    const content = heading.clientWidth - parseFloat(cs.paddingInlineStart) - parseFloat(cs.paddingInlineEnd);
    const wrap = heading.style.overflowWrap;
    heading.style.overflowWrap = 'normal';
    heading.style.setProperty('--hp-promo-fit', `${BASE}px`);
    const widest = widestWord(heading);
    heading.style.overflowWrap = wrap;
    heading.style.removeProperty('--hp-promo-fit');

    // Fits at the node's size (or nothing to measure): leave the stylesheet's.
    if (!widest || !content || widest <= content) return;

    const fitted = (BASE * content) / widest - 0.5;
    // Too long even at FLOOR: leave the node's size (the word breaks). That
    // heading wants Width (mobile): 2 columns.
    if (fitted < FLOOR) return;
    heading.style.setProperty('--hp-promo-fit', `${Math.floor(fitted * 4) / 4}px`);
  };

  const fitAll = () => document.querySelectorAll(SELECTOR).forEach(fitOne);

  let frame = 0;
  const schedule = () => {
    cancelAnimationFrame(frame);
    frame = requestAnimationFrame(fitAll);
  };

  const start = () => {
    fitAll();
    if (document.fonts && document.fonts.ready) document.fonts.ready.then(fitAll);
    window.addEventListener('resize', schedule, { passive: true });
    MOBILE.addEventListener('change', fitAll);

    // Tiles arrive with infinite scroll and filter/sort swaps.
    const results = document.querySelectorAll('[data-hp-collection-results]');
    if ('MutationObserver' in window) {
      const observer = new MutationObserver(schedule);
      results.forEach((el) => observer.observe(el, { childList: true, subtree: true }));
    }
  };

  if (document.readyState === 'loading') document.addEventListener('DOMContentLoaded', start);
  else start();
})();
